import React, { useState } from 'react';
import {
  Card,
  Input,
  Button,
  Typography,
  Divider,
  message,
  Space,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  CloseOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { SourceScene } from '../../../types';
import { sourceStoriesApi } from '../../../services/api/sourceStories';

const { TextArea } = Input;
const { Title, Text } = Typography;

interface SceneInlineEditorProps {
  storyId: string;
  processId: string;
  scene: SourceScene;
  onSuccess: (updatedScene: SourceScene) => void;
  onCancel: () => void;
}

const SceneInlineEditor: React.FC<SceneInlineEditorProps> = ({
  storyId,
  processId,
  scene,
  onSuccess,
  onCancel,
}) => {
  const [summary, setSummary] = useState(scene.summary || '');
  const [keyMoments, setKeyMoments] = useState<string[]>(
    scene.keyMoments && scene.keyMoments.length > 0 
      ? [...scene.keyMoments] 
      : ['']
  );
  const [loading, setLoading] = useState(false);

  const addKeyMoment = () => {
    setKeyMoments([...keyMoments, '']);
  };

  const removeKeyMoment = (index: number) => {
    if (keyMoments.length > 1) {
      const newKeyMoments = [...keyMoments];
      newKeyMoments.splice(index, 1);
      setKeyMoments(newKeyMoments);
    }
  };

  const updateKeyMoment = (index: number, value: string) => {
    const newKeyMoments = [...keyMoments];
    newKeyMoments[index] = value;
    setKeyMoments(newKeyMoments);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const filteredKeyMoments = keyMoments
        .map(k => k.trim())
        .filter(k => k.length > 0);

      const result = await sourceStoriesApi.updateSourceScene(storyId, processId, scene.id, {
        summary: summary.trim(),
        keyMoments: filteredKeyMoments,
      });

      message.success('Scene updated successfully');
      onSuccess(result.data);
    } catch (error: any) {
      console.error('Failed to update scene:', error);
      message.error(error.response?.data?.error || 'Failed to update scene');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      size="small"
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={5} style={{ margin: 0 }}>Edit Scene Info</Title>
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={onCancel}
            size="small"
          />
        </div>
      }
      style={{ marginBottom: 16, border: '1px solid #1890ff', borderRadius: 8 }}
    >
      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 4 }}>
          <Text strong>Summary</Text>
        </div>
        <TextArea
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          placeholder="Enter scene summary..."
          autoSize={{ minRows: 2, maxRows: 6 }}
        />
      </div>

      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <Text strong>Key Moments</Text>
          <Button
            type="dashed"
            onClick={addKeyMoment}
            icon={<PlusOutlined />}
            size="small"
          >
            Add Moment
          </Button>
        </div>

        <Space direction="vertical" style={{ width: '100%' }}>
          {keyMoments.map((moment, index) => (
            <div key={index} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Input
                value={moment}
                onChange={(e) => updateKeyMoment(index, e.target.value)}
                placeholder={`Key moment ${index + 1}...`}
                prefix={<Text type="secondary">#{index + 1}</Text>}
              />
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => removeKeyMoment(index)}
                disabled={keyMoments.length <= 1}
              />
            </div>
          ))}
        </Space>
      </div>

      <Divider style={{ margin: '12px 0' }} />

      <Button
        type="primary"
        icon={<SaveOutlined />}
        onClick={handleSave}
        loading={loading}
        block
      >
        Save Scene
      </Button>
    </Card>
  );
};

export default SceneInlineEditor;
